import { FC, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tooltip } from '../../../components/ui/tooltip';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';
import { IInjury } from '../../../models/injury';
import { INJURY_BODY_ZONE_LABEL, INJURY_SEVERITY } from '../../../enums/injuries';
import { InjuryRegistrationModal } from '../InjuryRegistrationForm/InjuryRegistrationModal';
import { InjuryReturnModal } from './InjuryReturnModal';
import { ShowByRole } from '../../../shared/ShowByRole';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import dayjs from 'dayjs';
import styles from './InjuryTimeline.module.scss';

interface InjuryTimelineProps {
  athleteId: string;
  teamId: string;
  period?: string;
}

const SEVERITY_CONFIG: Record<INJURY_SEVERITY, { color: string; bg: string; label: string }> = {
  [INJURY_SEVERITY.SLIGHT]: { color: 'var(--ri-good)', bg: 'var(--c-success-bg)', label: 'Lieve' },
  [INJURY_SEVERITY.MINIMAL]: { color: 'var(--ri-good)', bg: 'var(--c-success-bg)', label: 'Minima' },
  [INJURY_SEVERITY.MILD]: { color: 'var(--ri-warning)', bg: 'var(--c-warning-bg)', label: 'Moderata lieve' },
  [INJURY_SEVERITY.MODERATE]: { color: 'var(--ri-warning)', bg: 'var(--c-warning-bg)', label: 'Moderata' },
  [INJURY_SEVERITY.SEVERE]: { color: 'var(--ri-critical)', bg: 'var(--c-error-bg)', label: 'Severa' },
  [INJURY_SEVERITY.CAREER_ENDING]: { color: 'var(--ri-critical)', bg: 'var(--c-error-bg)', label: 'Fine carriera' },
  [INJURY_SEVERITY.DA_VALUTARE]: { color: 'var(--c-text-1)', bg: 'var(--c-icon-wrap-bg)', label: 'Da valutare' },
};

const InjuryCard: FC<{
  injury: IInjury;
  athleteId: string;
  teamId: string;
}> = ({ injury, athleteId, teamId }) => {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);

  const cfg = SEVERITY_CONFIG[(injury.severityEstimated ?? injury.severity)!] ?? SEVERITY_CONFIG[INJURY_SEVERITY.DA_VALUTARE];
  const zoneLabel = INJURY_BODY_ZONE_LABEL[injury.bodyZone] ?? injury.bodyZone;

  const daysLost = injury.returnDate
    ? Math.floor((new Date(injury.returnDate).getTime() - new Date(injury.injuryDate).getTime()) / 86400000)
    : injury.isActive
    ? Math.floor((Date.now() - new Date(injury.injuryDate).getTime()) / 86400000)
    : null;

  return (
    <div className={styles.card}>
      {/* Indicatore severity laterale */}
      <div className={styles.severityBar} style={{ background: cfg.color }} />

      <div className={styles.cardBody}>
        {/* Header riga */}
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            {/* Dot severity */}
            <div
              className={styles.severityDot}
              style={{ background: cfg.color, boxShadow: `0 0 0 4px ${cfg.bg}` }}
            />
            <div>
              <div className={styles.cardTitle}>
                {zoneLabel}
                {injury.injuryType && <span className={styles.injuryType}> · {injury.injuryType}</span>}
              </div>
              <div className={styles.cardMeta}>
                {dayjs(injury.injuryDate).format('DD/MM/YYYY')}
                {(injury.severityEstimated ?? injury.severity) && (injury.severityEstimated ?? injury.severity) !== INJURY_SEVERITY.DA_VALUTARE && (
                  <>{' · '}<span style={{ color: cfg.color }}>{cfg.label}</span></>
                )}
              </div>
            </div>
          </div>

          <div className={styles.cardHeaderRight}>
            {/* Badge stato */}
            {injury.isActive ? (
              <span className={styles.badgeActive}>
                Attivo · {daysLost}gg
              </span>
            ) : (
              <span className={styles.badgeClosed}>
                Chiuso
                {daysLost != null && ` · ${daysLost}gg`}
              </span>
            )}
            {/* Badge recidiva */}
            {injury.isRecurrence && (
              <span className={styles.badgeRecurrence}>Recidiva</span>
            )}
            {/* Badge RTP */}
            {injury.rtpProtocolId && (
              <Tooltip content={`Protocollo RTP: ${injury.rtpProtocolId}`}>
                <span className={styles.badgeRtp}>RTP</span>
              </Tooltip>
            )}
          </div>
        </div>

        {/* Azioni */}
        <div className={styles.cardActions}>
          <CustomButton color="outline" size="sm" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Nascondi dettagli' : 'Dettagli'}
          </CustomButton>
          <ShowByRole roles={['isDirector', 'isCoach']}>
            <CustomButton color="outline" size="sm" onClick={() => setEditOpen(true)}>
              Modifica
            </CustomButton>
            {injury.isActive && (
              <CustomButton color="secondary" size="sm" onClick={() => setReturnOpen(true)}>
                Registra ritorno
              </CustomButton>
            )}
          </ShowByRole>
        </div>

        {/* Dettagli espandibili */}
        {expanded && (
          <div className={styles.cardDetails}>
            {injury.diagnosis && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Diagnosi</span>
                <span>{injury.diagnosis}</span>
              </div>
            )}
            {injury.mechanism && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Meccanismo</span>
                <span>{injury.mechanism}</span>
              </div>
            )}
            {injury.context && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Contesto</span>
                <span>{injury.context}</span>
              </div>
            )}
            {injury.treatingPhysio && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Fisioterapista</span>
                <span>{injury.treatingPhysio}</span>
              </div>
            )}
            {injury.description && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Note</span>
                <span>{injury.description}</span>
              </div>
            )}
            {injury.expectedReturnDate && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Ritorno previsto</span>
                <span>{dayjs(injury.expectedReturnDate).format('DD/MM/YYYY')}</span>
              </div>
            )}
            {injury.returnDate && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Data ritorno effettiva</span>
                <span>{dayjs(injury.returnDate).format('DD/MM/YYYY')}</span>
              </div>
            )}
            {injury.loadSnapshot && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Carico al momento</span>
                <span>
                  ACWR {injury.loadSnapshot.acwr?.toFixed(2) ?? '–'}
                  {' · '}sRPE {injury.loadSnapshot.sRPE ?? '–'}
                  {' · '}Carico settim. {injury.loadSnapshot.weeklyLoad ?? '–'}
                </span>
              </div>
            )}
            {injury.notes.length > 0 && (
              <div className={styles.notesList}>
                <span className={`${styles.detailLabel} ${styles.notesListLabel}`}>Note cliniche</span>
                {injury.notes.map((n) => (
                  <div key={n._id} className={styles.noteItem}>
                    <span className={styles.noteAuthor}>{n.authorName ?? n.authorId}</span>
                    <span className={styles.noteDate}>{dayjs(n.createdAt).format('DD/MM/YYYY')}</span>
                    <p className={styles.noteContent}>{n.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {editOpen && (
        <InjuryRegistrationModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          athleteId={athleteId}
          teamId={teamId}
          editingInjury={injury}
        />
      )}
      {returnOpen && (
        <InjuryReturnModal
          isOpen={returnOpen}
          onClose={() => setReturnOpen(false)}
          injuryId={injury._id}
          athleteId={athleteId}
        />
      )}
    </div>
  );
};

export const InjuryTimeline: FC<InjuryTimelineProps> = ({ athleteId, teamId, period }) => {
  const endpoints = useInjuryEndpoints();

  const { data: injuries, isLoading, isError, error, refetch } = useQuery<IInjury[]>({
    queryKey: ['athlete-injuries', athleteId, period],
    queryFn: async () => {
      const { data } = await endpoints.getAthleteInjuries(athleteId, period ? { period } : undefined);
      return data;
    },
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <LeopartLoader size="sm" />
        <span>Caricamento infortuni...</span>
      </div>
    );
  }

  if (isError) {
    return <ChartErrorState onRetry={refetch} message={(error as Error)?.message} />;
  }

  if (!injuries || injuries.length === 0) {
    return (
      <div className={styles.empty}>
        <p>Nessun infortunio registrato nel periodo selezionato.</p>
      </div>
    );
  }

  // Ordine cronologico decrescente (più recente prima)
  const sorted = [...injuries].sort(
    (a, b) => new Date(b.injuryDate).getTime() - new Date(a.injuryDate).getTime()
  );

  return (
    <div className={styles.timeline}>
      <div className={styles.timelineHeader}>
        <h3 className={styles.timelineTitle}>Timeline infortuni</h3>
        <span className={styles.timelineCount}>{injuries.length} {injuries.length !== 1 ? 'eventi' : 'evento'}</span>
      </div>
      <div className={styles.timelineList}>
        {sorted.map((injury) => (
          <InjuryCard key={injury._id} injury={injury} athleteId={athleteId} teamId={teamId} />
        ))}
      </div>
    </div>
  );
};
